import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";

const customModalStyles = {
  content: {
    padding: "20px",
    width: "80%",
    maxWidth: "600px",
    margin: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
};

const emptyForm = { name: "", description: "", vision: "", mission: "", order: "" };

const Faculty = () => {
  const [faculties, setFaculties] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  useEffect(() => {
    fetchFaculties();
  }, []);

  const fetchFaculties = async () => {
    setFetchLoading(true);
    try {
      const response = await axiosInstance.get("faculty/all");
      setFaculties(response.data.data || []);
    } catch (error) {
      console.error("Error fetching faculties:", error);
    }
    setFetchLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setButtonLoading(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        vision: formData.vision,
        mission: formData.mission,
      };
      if (formData.order !== "" && formData.order !== null && formData.order !== undefined) {
        payload.order = Number(formData.order);
      }
      await axiosInstance.post("faculty/create", payload);
      await fetchFaculties();
      setShowCreateModal(false);
      setFormData(emptyForm);
      Swal.fire("Success!", "Faculty created successfully!", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to create faculty",
        "error"
      );
    }
    setButtonLoading(false);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setButtonLoading(true);
    try {
      await axiosInstance.post(`faculty/change/${editId}`, {
        name: formData.name,
        description: formData.description,
        vision: formData.vision,
        mission: formData.mission,
        order: Number(formData.order || 0),
        _method: "PUT",
      });
      await fetchFaculties();
      setShowEditModal(false);
      setFormData(emptyForm);
      Swal.fire("Success!", "Faculty updated successfully!", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update faculty",
        "error"
      );
    }
    setButtonLoading(false);
  };

  const handleView = async (id) => {
    try {
      setShowEditModal(true);
      setEditId(id);
      const response = await axiosInstance.get(`faculty/single/${id}`);
      const data = response?.data?.data;
      setFormData({
        name: data?.name || "",
        description: data?.description || "",
        vision: data?.vision || "",
        mission: data?.mission || "",
        order: data?.order ?? "",
      });
    } catch (error) {
      Swal.fire("Error", "Failed to load faculty", "error");
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });
      if (!result.isConfirmed) return;

      await axiosInstance.delete(`faculty/delete/${id}`);
      await fetchFaculties();
      Swal.fire("Deleted!", "Faculty deleted successfully", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete faculty",
        "error"
      );
    }
  };

  const renderForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit}>
      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
          Name
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          style={{
            width: "100%",
            padding: "8px",
            fontSize: "14px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />
      </div>
      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
          Order
        </label>
        <input
          type="number"
          name="order"
          min="0"
          value={formData.order}
          onChange={handleChange}
          placeholder="Lower numbers appear first"
          style={{
            width: "100%",
            padding: "8px",
            fontSize: "14px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />
      </div>
      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          style={{
            width: "100%",
            padding: "8px",
            fontSize: "14px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            height: "100px",
          }}
        />
      </div>
      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
          Vision
        </label>
        <textarea
          name="vision"
          value={formData.vision}
          onChange={handleChange}
          placeholder="Faculty Vision statement..."
          style={{
            width: "100%",
            padding: "8px",
            fontSize: "14px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            height: "90px",
          }}
        />
      </div>
      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
          Mission
        </label>
        <textarea
          name="mission"
          value={formData.mission}
          onChange={handleChange}
          placeholder="Faculty Mission statement..."
          style={{
            width: "100%",
            padding: "8px",
            fontSize: "14px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            height: "90px",
          }}
        />
      </div>
      <button
        type="submit"
        style={{
          padding: "10px 20px",
          backgroundColor: "#4CAF50",
          color: "#fff",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
          marginTop: "10px",
        }}
      >
        {buttonLoading ? <CircularProgress size={24} color="inherit" /> : submitLabel}
      </button>
    </form>
  );

  return (
    <div>
      <button
        onClick={() => {
          setFormData(emptyForm);
          setShowCreateModal(true);
        }}
        style={{
          padding: "10px 20px",
          backgroundColor: "#4CAF50",
          color: "#fff",
          border: "none",
          borderRadius: "5px",
          marginBottom: "20px",
          cursor: "pointer",
        }}
      >
        Create New Faculty
      </button>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
        {fetchLoading ? (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 9999,
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          faculties.map((faculty) => (
            <div
              key={faculty._id}
              style={{
                width: "280px",
                padding: "15px",
                borderRadius: "8px",
                backgroundColor: "#fff",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              <h3 style={{ marginTop: 0 }}>{faculty.name}</h3>
              <p style={{ margin: "0 0 8px", fontSize: "13px", color: "#4a90e2" }}>
                Order: {faculty.order ?? 0}
              </p>
              <p
                style={{
                  color: "#555",
                  fontSize: "14px",
                  maxHeight: "90px",
                  overflow: "hidden",
                }}
              >
                {faculty.description || "No description"}
              </p>
              <Actions
                courseId={faculty._id}
                onEdit={handleView}
                onDelete={handleDelete}
              />
            </div>
          ))
        )}
      </div>

      <Modal
        isOpen={showCreateModal}
        onRequestClose={() => setShowCreateModal(false)}
        style={customModalStyles}
        contentLabel="Create Faculty"
      >
        <h2>Create New Faculty</h2>
        {renderForm(handleCreate, "Create")}
      </Modal>

      <Modal
        isOpen={showEditModal}
        onRequestClose={() => {
          setShowEditModal(false);
          setFormData(emptyForm);
        }}
        style={customModalStyles}
        contentLabel="Edit Faculty"
      >
        <h2>Edit Faculty</h2>
        {renderForm(handleEdit, "Update")}
      </Modal>
    </div>
  );
};

export default Faculty;
